import TomSelect from 'tom-select';
import { loadFlatpickrRu } from '../utils/load-flatpickr-ru';

/**
 * @param {HTMLSelectElement} select
 * @return {string}
 */
const getPlaceholderFromSelect = (select) => {
	const blankOption = Array.from(select.options).find(
		(option) => '' === option.value
	);

	return blankOption?.textContent?.trim() || '';
};

/**
 * @param {import('tom-select').default} ts
 * @param {HTMLSelectElement} select
 * @return {void}
 */
const loadOptionsFromNative = (ts, select) => {
	ts.clear(true);
	ts.clearOptions();

	Array.from(select.options).forEach((option) => {
		if ('' === option.value) {
			return;
		}

		ts.addOption({
			value: option.value,
			text: option.textContent || '',
		});
	});

	ts.refreshOptions(false);
};

/**
 * @param {HTMLSelectElement} select
 * @return {void}
 */
const enhanceQuestionnaireSelect = (select) => {
	if (!(select instanceof HTMLSelectElement) || select.tomselect) {
		return;
	}

	const placeholderText = getPlaceholderFromSelect(select);

	new TomSelect(select, {
		allowEmptyOption: true,
		create: false,
		maxOptions: null,
		maxItems: 1,
		hideSelected: false,
		closeAfterSelect: true,
		placeholder: placeholderText,
		onInitialize() {
			loadOptionsFromNative(this, select);

			if (select.value) {
				this.setValue(select.value, true);
			} else {
				this.clear(true);
				select.value = '';
			}
		},
	});
};

/**
 * @param {HTMLInputElement} input
 * @param {string} placeholder
 * @return {void}
 */
const initDatePicker = async (input, placeholder) => {
	if (!input || input._flatpickr) {
		return;
	}

	if ('date' === input.type) {
		input.type = 'text';
	}

	input.setAttribute('placeholder', placeholder);
	input.setAttribute('autocomplete', 'off');
	input.dataset.inputInitialized = 'true';

	const { flatpickr, Russian } = await loadFlatpickrRu();

	if (input._flatpickr) {
		return;
	}

	flatpickr(input, {
		dateFormat: 'Y-m-d',
		allowInput: true,
		disableMobile: true,
		locale: Russian,
	});
};

/**
 * @param {HTMLElement} block
 * @return {void}
 */
const syncSymptomsField = (block) => {
	const symptomsField = block.querySelector('input[name="symptoms-data"]');

	if (!(symptomsField instanceof HTMLInputElement)) {
		return;
	}

	const selected = Array.from(
		block.querySelectorAll('input[name="symptoms[]"]:checked')
	).map((input) => ({
		slug: input.value,
		label: input.dataset.label || input.value,
	}));

	symptomsField.value = JSON.stringify(selected);
};

/**
 * @param {HTMLElement} errorEl
 * @param {string} message
 * @return {void}
 */
const setSymptomError = (errorEl, message) => {
	if (!(errorEl instanceof HTMLElement)) {
		return;
	}

	errorEl.textContent = message;
	errorEl.hidden = !message;
};

/**
 * @param {HTMLSelectElement} select
 * @return {void}
 */
const syncNativeSelectPlaceholder = (select) => {
	select.classList.toggle('form-select--placeholder', '' === select.value);
};

/**
 * @param {HTMLSelectElement} select
 * @return {void}
 */
const setupNativeSelectPlaceholder = (select) => {
	syncNativeSelectPlaceholder(select);

	if ('true' === select.dataset.placeholderSyncInitialized) {
		return;
	}

	select.dataset.placeholderSyncInitialized = 'true';
	select.addEventListener('change', () => {
		syncNativeSelectPlaceholder(select);
	});
};

/**
 * @param {HTMLElement} block
 * @return {void}
 */
const setupPatientQuestionnaireBlock = (block) => {
	const symptomError = block.querySelector(
		'[data-patient-questionnaire-symptom-error]'
	);
	const form = block.querySelector('form');

	if (!(form instanceof HTMLFormElement)) {
		return;
	}

	block.querySelectorAll('select.form-select').forEach((select) => {
		if (!(select instanceof HTMLSelectElement)) {
			return;
		}

		if ('gender' === select.name) {
			setupNativeSelectPlaceholder(select);
			return;
		}

		enhanceQuestionnaireSelect(select);
	});

	const birthDate = form.querySelector('input[name="birth-date"]');
	const passportDate = form.querySelector('input[name="passport-date"]');

	if (birthDate instanceof HTMLInputElement) {
		void initDatePicker(birthDate, 'Дата рождения');
	}

	if (passportDate instanceof HTMLInputElement) {
		void initDatePicker(passportDate, 'Дата выдачи');
	}

	if (window.Input) {
		const inputs = form.querySelectorAll(
			'input:not([data-input-initialized])'
		);

		inputs.forEach((input) => {
			input.dataset.inputInitialized = 'true';
		});

		new window.Input().listenner(Array.from(inputs));
	}

	block.addEventListener('change', (event) => {
		const target = event.target;

		if (
			target instanceof HTMLInputElement &&
			'symptoms[]' === target.name
		) {
			syncSymptomsField(block);
			setSymptomError(symptomError, '');
		}
	});

	form.addEventListener(
		'submit',
		(event) => {
			syncSymptomsField(block);

			const hasSymptoms = form.querySelector(
				'input[name="symptoms[]"]:checked'
			);

			if (!hasSymptoms) {
				event.preventDefault();
				event.stopPropagation();
				setSymptomError(
					symptomError,
					block.dataset.symptomError ||
						'Выберите хотя бы один симптом.'
				);
				form.querySelector('input[name="symptoms[]"]')?.focus();
			}
		},
		true
	);

	syncSymptomsField(block);
};

/**
 * Patient questionnaire page CF7 enhancements.
 *
 * @return {void}
 */
export function initPatientQuestionnaire() {
	document
		.querySelectorAll('[data-patient-questionnaire]')
		.forEach((block) => {
			if (block instanceof HTMLElement) {
				setupPatientQuestionnaireBlock(block);
			}
		});
}
